import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Loader2, UserPlus, Edit3, Trash2, Link2, Copy, Check, Send, KeyRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import MemberAccountPanel from '../components/team/MemberAccountPanel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATIONS = [
  'Poste Four à Pain',
  'Poste Mezzés Froids-Desserts',
  'Poste Josper',
  'Poste Mezzés Chauds',
  'Poste Préparation',
];

const stationColors = {
  'Poste Four à Pain': 'bg-amber-50 text-amber-700 border border-amber-300',
  'Poste Mezzés Froids-Desserts': 'bg-neon-pink/10 text-neon-pink border border-neon-pink/30',
  'Poste Josper': 'bg-orange-500/10 text-orange-600 border border-orange-500/30',
  'Poste Mezzés Chauds': 'bg-red-500/10 text-red-600 border border-red-500/30',
  'Poste Préparation': 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30',
};

const TeamPage = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_manager;
  const [accountMember, setAccountMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [inactiveMembers, setInactiveMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [form, setForm] = useState({ name: '', station: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState(null);
  const [inviteDialog, setInviteDialog] = useState({ open: false, link: '', memberName: '' });
  const [copied, setCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [resetDialog, setResetDialog] = useState({ open: false, memberId: null, memberName: '' });
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/teams/`, { params: { include_inactive: true } });
      const all = res.data.data || [];
      setMembers(all.filter(m => m.active !== false));
      setInactiveMembers(all.filter(m => m.active === false));
    } catch (error) { toast.error(formatApiError(error)); } finally { setLoading(false); }
  }, []);

  const handleReactivate = async (member) => {
    try {
      await axios.put(`${API}/teams/${member.id}`, { active: true });
      toast.success(`${member.name} réactivé(e)`);
      fetchMembers();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const openDialog = (member = null) => {
    setEditMember(member);
    setEmailConfirm(null);
    setForm(member ? { name: member.name, station: member.station || '', email: member.email || '' } : { name: '', station: '', email: '' });
    setDialogOpen(true);
  };

  const handleSave = async (syncAccountEmail = false) => {
    if (!form.name.trim()) { toast.error('Le nom est requis'); return; }
    if (!form.station) { toast.error('Le poste est requis'); return; }
    try {
      setSaving(true);
      if (editMember) {
        await axios.put(`${API}/teams/${editMember.id}`, syncAccountEmail ? { ...form, sync_account_email: true } : form);
        toast.success(syncAccountEmail ? 'Membre modifié · adresse de connexion mise à jour' : 'Membre modifié');
      } else {
        await axios.post(`${API}/teams/`, { ...form, role: 'collaborateur' });
        toast.success('Membre ajouté !');
      }
      setEmailConfirm(null);
      setDialogOpen(false);
      fetchMembers();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.code === 'confirm_account_email') setEmailConfirm(detail);
      else toast.error(formatApiError(error));
    } finally { setSaving(false); }
  };

  const handleDelete = async (memberId) => {
    try {
      await axios.delete(`${API}/teams/${memberId}`);
      toast.success('Membre supprimé');
      fetchMembers();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleGenerateInvite = async (member) => {
    try {
      setGeneratingLink(true);
      const res = await axios.post(`${API}/teams/${member.id}/invite`);
      const link = res.data.data.invite_link;
      setInviteDialog({ open: true, link, memberName: member.name });
      setCopied(false);
    } catch (error) { toast.error(formatApiError(error)); } finally { setGeneratingLink(false); }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteDialog.link);
    setCopied(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopied(false), 3000);
  };

  const openResetDialog = (member) => {
    setResetDialog({ open: true, memberId: member.id, memberName: member.name });
    setNewPassword('');
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    try {
      setResetting(true);
      await axios.post(`${API}/auth/reset-password`, {
        member_id: resetDialog.memberId,
        new_password: newPassword,
      });
      toast.success(`Mot de passe réinitialisé pour ${resetDialog.memberName}`);
      setResetDialog({ open: false, memberId: null, memberName: '' });
      setNewPassword('');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setResetting(false);
    }
  };

  // Group by station
  const groupedMembers = members.reduce((acc, m) => {
    const station = m.station || 'Sans poste';
    if (!acc[station]) acc[station] = [];
    acc[station].push(m);
    return acc;
  }, {});

  if (user && !(user.permissions || []).includes('view_team')) return <Navigate to="/gerer" replace />;

  return (
    <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="team-page">
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">L'Équipe</h1>
            <p className="text-gray-500">Gérez votre brigade de cuisine</p>
          </div>
          {isAdmin && (
            <Button onClick={() => openDialog()} className="bg-neon-pink hover:bg-neon-pink/90 rounded-full shadow-neon-pink neon-glow-pink" data-testid="add-member-btn">
              <UserPlus size={18} className="mr-2" />Ajouter
            </Button>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-neon-pink/10 border border-neon-pink/30 rounded-2xl flex items-center justify-center"><Users className="text-neon-pink" size={28} /></div>
            <div><div className="font-heading text-3xl font-bold text-gray-900">{members.length}</div><p className="text-gray-500">membres dans la brigade</p></div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20"><Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-4" /><p className="text-gray-500">Chargement...</p></div>
        ) : members.length === 0 ? (
          <div className="text-center py-20"><Users className="w-16 h-16 text-gray-500 mx-auto mb-4" /><h3 className="font-heading text-xl font-bold text-gray-900 mb-2">Aucun membre</h3></div>
        ) : (
          <div className="space-y-8">
            {STATIONS.map(station => {
              const stationMembers = groupedMembers[station];
              if (!stationMembers || stationMembers.length === 0) return null;
              return (
                <div key={station}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${stationColors[station] || 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                      {station.replace('Poste ', '')}
                    </span>
                    <span className="text-gray-400 text-sm">({stationMembers.length})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stationMembers.map((member, index) => (
                      <motion.div key={member.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} whileHover={{ scale: 1.02 }} className="bg-white border border-gray-200 rounded-2xl p-6 card-hover group relative">
                        {isAdmin && (
                          <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openResetDialog(member)} className="w-8 h-8 rounded-lg text-purple-600 hover:bg-purple-50 flex items-center justify-center" data-testid={`reset-pwd-${member.id}`} title="Réinitialiser le mot de passe"><KeyRound size={14} /></button>
                            <button onClick={() => handleGenerateInvite(member)} className="w-8 h-8 rounded-lg text-emerald-600 hover:bg-emerald-500/10 flex items-center justify-center" data-testid={`invite-member-${member.id}`} title="Générer lien d'invitation"><Send size={14} /></button>
                            <button onClick={() => openDialog(member)} className="w-8 h-8 rounded-lg text-amber-700 hover:bg-amber-50 flex items-center justify-center" data-testid={`edit-member-${member.id}`}><Edit3 size={14} /></button>
                            <button onClick={() => handleDelete(member.id)} className="w-8 h-8 rounded-lg text-red-600 hover:bg-red-500/10 flex items-center justify-center" data-testid={`delete-member-${member.id}`}><Trash2 size={14} /></button>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Avatar className="w-14 h-14 border-2 border-amber-300">
                            <AvatarFallback className="bg-amber-50 text-amber-700 font-bold text-lg">{member.name.split(' ').map(n => n[0]).join('').toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-heading font-bold text-gray-900">{member.name}</h3>
                            <p className="text-sm text-gray-500">{member.station?.replace('Poste ', '') || 'Aucun poste'}</p>
                            {member.email && <p className="text-xs text-gray-500 mt-0.5">{member.email}</p>}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              );
            })}
            {/* Members without station */}
            {groupedMembers['Sans poste'] && groupedMembers['Sans poste'].length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-gray-100 text-gray-500 border border-gray-200">Sans poste</span>
                  <span className="text-gray-400 text-sm">({groupedMembers['Sans poste'].length})</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedMembers['Sans poste'].map((member, index) => (
                    <motion.div key={member.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} whileHover={{ scale: 1.02 }} className="bg-white border border-gray-200 rounded-2xl p-6 card-hover group relative">
                      {isAdmin && (
                        <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleGenerateInvite(member)} className="w-8 h-8 rounded-lg text-emerald-600 hover:bg-emerald-500/10 flex items-center justify-center" data-testid={`invite-member-${member.id}`}><Send size={14} /></button>
                          <button onClick={() => openDialog(member)} className="w-8 h-8 rounded-lg text-amber-700 hover:bg-amber-50 flex items-center justify-center" data-testid={`edit-member-${member.id}`}><Edit3 size={14} /></button>
                          <button onClick={() => handleDelete(member.id)} className="w-8 h-8 rounded-lg text-red-600 hover:bg-red-500/10 flex items-center justify-center" data-testid={`delete-member-${member.id}`}><Trash2 size={14} /></button>
                        </div>
                      )}
                      <div className="flex items-center gap-4">
                        <Avatar className="w-14 h-14 border-2 border-amber-300">
                          <AvatarFallback className="bg-amber-50 text-amber-700 font-bold text-lg">{member.name.split(' ').map(n => n[0]).join('').toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-heading font-bold text-gray-900">{member.name}</h3>
                          <p className="text-sm text-gray-500">Sans poste</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {isAdmin && inactiveMembers.length > 0 && (
          <details className="mt-8 bg-gray-50 border border-gray-200 rounded-2xl p-4" data-testid="inactive-members">
            <summary className="cursor-pointer text-sm font-medium text-gray-700 min-h-[32px] flex items-center">{inactiveMembers.length} membre{inactiveMembers.length > 1 ? 's' : ''} désactivé{inactiveMembers.length > 1 ? 's' : ''} (masqués de la brigade, historique conservé)</summary>
            <div className="space-y-2 mt-3">
              {inactiveMembers.map(m => (
                <div key={m.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-3 py-2" data-testid={`inactive-member-${m.id}`}>
                  <div><p className="font-medium text-gray-800">{m.name}</p><p className="text-xs text-gray-500">{(m.station || 'Sans poste').replace('Poste ', '')}{m.email ? ` · ${m.email}` : ''}</p></div>
                  <Button variant="outline" size="sm" onClick={() => handleReactivate(m)} className="h-9 rounded-full border-gray-300 text-gray-700" data-testid={`reactivate-member-${m.id}`}>Réactiver</Button>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Add/Edit Member Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader><DialogTitle className="font-heading text-xl text-gray-900">{editMember ? 'Modifier' : 'Ajouter'} un membre</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-700">Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Marco Rossi" className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400" data-testid="member-name-input" />
            </div>
            <div>
              <Label className="text-gray-700">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Ex: marco@restaurant.com" className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400" data-testid="member-email-input" />
            </div>
            <div>
              <Label className="text-gray-700">Poste</Label>
              <Select key={`station-${editMember?.id || 'new'}-${form.station}`} value={form.station} onValueChange={(v) => setForm(prev => ({ ...prev, station: v }))}>
                <SelectTrigger className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-800" data-testid="member-station-select"><SelectValue placeholder="Sélectionner un poste" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200" position="popper" sideOffset={4}>
                  {STATIONS.map(s => <SelectItem key={s} value={s} className="text-gray-800">{s.replace('Poste ', '')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {emailConfirm && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 space-y-2" data-testid="email-confirm-box">
              <p className="text-sm text-amber-900">{emailConfirm.message}</p>
              <p className="text-xs text-amber-800">Le compte, ses tâches et son historique sont conservés ; seule l'adresse de connexion change ({emailConfirm.account_email} → {emailConfirm.new_email}).</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEmailConfirm(null)} className="flex-1 h-10 rounded-full border-amber-300 text-amber-900" data-testid="email-confirm-cancel-btn">Non, annuler</Button>
                <Button onClick={() => handleSave(true)} disabled={saving} className="flex-1 h-10 rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="email-confirm-accept-btn">{saving ? <Loader2 className="animate-spin" size={16} /> : 'Oui, mettre à jour'}</Button>
              </div>
            </div>
          )}
          {editMember && (
            <button type="button" onClick={() => setAccountMember(editMember)} className="w-full text-left border border-dashed border-gray-300 rounded-xl px-3 py-2.5 text-sm text-gray-700 hover:border-neon-pink min-h-[44px]" data-testid="open-account-panel-btn">
              <span className="font-medium">Compte de connexion</span> — {editMember.user_id ? `associé (${editMember.email || 'email inconnu'})` : 'aucun compte associé'} · rôle, statut, permissions →
            </button>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200">Annuler</Button>
            <Button onClick={() => handleSave(false)} disabled={saving || !form.name.trim() || !form.station} className="flex-1 bg-neon-pink hover:bg-neon-pink/90 rounded-full shadow-neon-pink" data-testid="save-member-btn">
              {saving ? <Loader2 className="animate-spin" size={18} /> : (editMember ? 'Modifier' : 'Ajouter')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Link Dialog */}
      <Dialog open={inviteDialog.open} onOpenChange={(open) => !open && setInviteDialog({ open: false, link: '', memberName: '' })}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-gray-900 flex items-center gap-2">
              <Link2 size={20} className="text-emerald-600" />
              Lien d'invitation
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Partagez ce lien via WhatsApp à <span className="text-neon-pink font-semibold">{inviteDialog.memberName}</span> pour qu'il puisse créer son mot de passe et accéder à l'application.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-gray-100 border border-gray-200 rounded-xl p-4 break-all">
              <p className="text-sm text-gray-700 font-mono">{inviteDialog.link}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setInviteDialog({ open: false, link: '', memberName: '' })} className="flex-1 rounded-full bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200">
              Fermer
            </Button>
            <Button onClick={handleCopyLink} className="flex-1 bg-emerald-500 hover:bg-emerald-600 rounded-full text-gray-900 gap-2" data-testid="copy-invite-link-btn">
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copié !' : 'Copier le lien'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(open) => !open && setResetDialog({ open: false, memberId: null, memberName: '' })}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-gray-900 flex items-center gap-2">
              <KeyRound size={20} className="text-purple-600" />
              Reinitialiser le mot de passe
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Nouveau mot de passe pour <span className="text-neon-pink font-semibold">{resetDialog.memberName}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-gray-700">Nouveau mot de passe</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 caractères"
                className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400"
                data-testid="reset-password-input"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setResetDialog({ open: false, memberId: null, memberName: '' })} className="flex-1 rounded-full bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200">
              Annuler
            </Button>
            <Button onClick={handleResetPassword} disabled={resetting || newPassword.length < 8} className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-full text-white gap-2" data-testid="confirm-reset-btn">
              {resetting ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              Confirmer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <MemberAccountPanel member={accountMember} open={!!accountMember} onOpenChange={(v) => { if (!v) setAccountMember(null); }} onChanged={fetchMembers} />
    </div>
  );
};

export default TeamPage;
